import React, { useState, useEffect, useRef, useCallback } from "react";
import { Send, Phone } from "lucide-react";
import "../App.css";
import { StructuredRenderer } from "../components/StructuredResponse";
import { BRAND_LOGO_DATA_URL } from "../constants/branding";
import { apiClient, getServerErrorMessage } from "../utils/apiClient";
import { requestUserLocation, isGeolocationAvailable } from "../services/geolocationService";
import ManagerContactWidget from "../components/ManagerContactWidget";
import { isFuzzyLocationQuery } from "../utils/fuzzyMatch";

/**
 * ChatbotPageStructured
 * Enhanced chatbot page with structured LLM responses
 * Supports HTML, images, cards, lists, and suggestions
 */
function ChatbotPageStructured() {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [trackingSessionId, setTrackingSessionId] = useState(null);
  const [sessionData, setSessionData] = useState(null);
  const [initialOptions, setInitialOptions] = useState([]);
  const [isManagerWidgetOpen, setIsManagerWidgetOpen] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Request deduplication: Track pending requests to prevent duplicates
  const pendingRequests = useRef(new Set());
  const lastRequestTime = useRef(0);
  const requestDebounceDelay = 300; // 300ms debounce delay

  // Generate unique request ID for deduplication
  const generateRequestId = (message, interactionType) => {
    const normalizedMessage = message.trim().toLowerCase();
    const timestamp = Date.now();
    return `${normalizedMessage}-${interactionType}-${timestamp}`;
  };

  // Check if a request is a duplicate (same message within debounce window)
  const isDuplicateRequest = (message, interactionType) => {
    const now = Date.now();
    const normalizedMessage = message.trim().toLowerCase();
    const requestKey = `${normalizedMessage}-${interactionType}`;

    // Check if there's a pending request with the same message
    if (pendingRequests.current.has(requestKey)) {
      console.warn("🚫 Duplicate request blocked (already pending):", requestKey);
      return true;
    }

    // Check if the same request was sent too recently (within debounce delay)
    if (now - lastRequestTime.current < requestDebounceDelay) {
      console.warn("🚫 Duplicate request blocked (too soon):", requestKey, `${now - lastRequestTime.current}ms ago`);
      return true;
    }

    return false;
  };

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const startChat = useCallback(async () => {
    try {
      setIsLoading(true);

      // Extract query parameters from URL
      const urlParams = new URLSearchParams(window.location.search);
      const location = urlParams.get('location');
      const city = urlParams.get('city');
      const tableNumberFromQuery = urlParams.get('t');
      const pathToken = window.location.pathname
        .replace(/^\/+|\/+$/g, "")
        .trim();
      // Reuse existing tableNumber field for short-code URLs like /1X3CV7
      const tableNumber = tableNumberFromQuery || (pathToken && !location && !city ? pathToken : null);

      // Step 1: Initialize session (creates session IDs) with query parameters
      const initResponse = await apiClient.post("/api/chat/init", {
        location,
        city,
        tableNumber,
      });
      const newSessionId = initResponse.data.sessionId;
      const newTrackingSessionId = initResponse.data.trackingSessionId;
      const newSessionData = initResponse.data.sessionData;

      setSessionId(newSessionId);
      setTrackingSessionId(newTrackingSessionId);
      setSessionData(newSessionData);

      // Step 2: Fetch welcome message (after session is initialized)
      // Get user's timezone offset in minutes (e.g., IST = -330 for UTC+5:30)
      const timezoneOffset = new Date().getTimezoneOffset();
      
      const welcomeResponse = await apiClient.post("/api/chat/welcome", {
        sessionId: newSessionId,
        trackingSessionId: newTrackingSessionId,
        timezoneOffset: timezoneOffset,
      });

      // Display welcome messages
      const welcomeMessages = welcomeResponse.data.messages || [];
      let maxDelay = 0;

      if (welcomeMessages.length > 0) {
        welcomeMessages.forEach((message) => {
          const delay = message.delay || 0;
          maxDelay = Math.max(maxDelay, delay);
          setTimeout(() => {
            setMessages((prev) => [
              ...prev,
              {
                role: message.role,
                content: message.content,
                timestamp: message.timestamp,
                type: "text",
              },
            ]);
          }, delay);
        });
      }

      // Set initial options after all welcome messages have been displayed
      setTimeout(() => {
        if (welcomeResponse.data.options && Array.isArray(welcomeResponse.data.options)) {
          // Convert options format to chip format for consistency
          const chips = welcomeResponse.data.options.map((opt) => ({
            label: opt.text,
            action: opt.action,
            source: opt.source || "backend",
            type: opt.type || "other",
          }));
          setInitialOptions(chips);
        }
      }, maxDelay + 100); // Add small buffer to ensure last message is rendered
    } catch (error) {
      console.error("Failed to start chat:", error?.response?.data || error);
      const fallbackContent = getServerErrorMessage(
        error,
        "I'm having trouble connecting right now. Please try again in a moment.",
      );

      setMessages([
        {
          role: "assistant",
          content: fallbackContent,
          timestamp: new Date().toISOString(),
          type: "text",
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Start chat session when component mounts
  useEffect(() => {
    if (!sessionId) {
      startChat();
    }
  }, [sessionId, startChat]);

  const sendMessage = async (
    message,
    contextData = null,
    interactionType = "user_input",
    origin = "llm",
    locationData = null,
    buttonType = null,
  ) => {
    if (!message?.trim()) return;

    // Check for duplicate requests
    if (isDuplicateRequest(message, interactionType)) {
      console.warn("⚠️ Duplicate request prevented:", message);
      return;
    }

    const userMessage = {
      role: "user",
      content: message,
      timestamp: new Date().toISOString(),
      type: "text",
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputMessage("");
    setIsLoading(true);

    await sendMessageToBackend(message, contextData, interactionType, origin, locationData, buttonType);
  };

  const renderStructuredResponse = (responseData) => {
    if (!responseData) {
      console.error("=== CHATBOT ERROR: No response data provided ===");
      return { handled: false, reason: "noData" };
    }

    if (responseData.trackingSessionId) {
      console.log("Updating trackingSessionId:", responseData.trackingSessionId);
      setTrackingSessionId(responseData.trackingSessionId);
    }

    // Update session data if provided
    if (responseData.sessionData) {
      console.log("Updating sessionData:", responseData.sessionData);
      setSessionData(responseData.sessionData);
    }

    // Handle responses with messages array (e.g., menu requests, welcome messages)
    if (responseData.messages && Array.isArray(responseData.messages)) {
      console.log("=== CHATBOT DEBUG: Handling messages array response ===");
      responseData.messages.forEach((msg) => {
        const botMessage = {
          role: msg.role || "assistant",
          content: msg.content,
          timestamp: msg.timestamp || new Date().toISOString(),
          type: msg.type || "text",
        };
        setMessages((prev) => [...prev, botMessage]);
      });
      
      // Set options if provided
      if (responseData.options && Array.isArray(responseData.options)) {
        setInitialOptions(responseData.options);
      }
      
      return { handled: true };
    }

    if (!responseData.structuredResponse) {
      console.error("=== CHATBOT ERROR: No structuredResponse in response ===");
      console.error("Full response.data:", responseData);
      console.error("Keys in response.data:", Object.keys(responseData));
      return { handled: false, reason: "missingStructuredResponse" };
    }

    const { structuredResponse } = responseData;

    if (!structuredResponse.blocks || !Array.isArray(structuredResponse.blocks)) {
      console.error("=== CHATBOT ERROR: Invalid blocks in structuredResponse ===");
      console.error("structuredResponse:", structuredResponse);
      console.error("Type of structuredResponse:", typeof structuredResponse);
      console.error("structuredResponse.blocks:", structuredResponse.blocks);
      console.error("Type of blocks:", typeof structuredResponse?.blocks);
      console.error("Is array:", Array.isArray(structuredResponse?.blocks));

      return { handled: false, reason: "invalidBlocks" };
    }

    if (structuredResponse.blocks.length === 0) {
      console.warn("=== CHATBOT WARNING: blocks array is empty ===");
      return { handled: false, reason: "emptyBlocks" };
    }

    console.log("=== CHATBOT DEBUG: Validation passed, creating bot message ===");
    console.log("blocks count:", structuredResponse.blocks.length);

    structuredResponse.blocks.forEach((block, idx) => {
      console.log(`Block ${idx}:`, block?.type || "NO TYPE", block);
    });

    const botMessage = {
      role: "assistant",
      timestamp: new Date().toISOString(),
      type: "structured",
      structuredResponse,
      metadata: responseData.metadata || {},
      trackingSessionId: responseData.trackingSessionId || trackingSessionId,
    };

    console.log("=== CHATBOT DEBUG: Bot message created ===");
    try {
      console.log("botMessage (stringified):", JSON.stringify(botMessage, null, 2));
    } catch (msgStringifyError) {
      console.error("Cannot stringify botMessage:", msgStringifyError);
    }

    setMessages((prev) => {
      console.log("=== CHATBOT DEBUG: Adding message to state ===");
      console.log("Previous messages count:", prev.length);
      const newMessages = [...prev, botMessage];
      console.log("New messages count:", newMessages.length);
      return newMessages;
    });

    console.log("=== CHATBOT DEBUG: setMessages called successfully ===");
    return { handled: true };
  };

  const sendMessageToBackend = async (
    message,
    contextData = null,
    interactionType = "user_input",
    origin = "llm",
    locationData = null,
    buttonType = null,
  ) => {
    // Generate unique request key for tracking
    const normalizedMessage = message.trim().toLowerCase();
    const requestKey = `${normalizedMessage}-${interactionType}`;

    // Add to pending requests
    pendingRequests.current.add(requestKey);
    lastRequestTime.current = Date.now();

    try {
      console.log("=== SENDING MESSAGE TO BACKEND ===");
      console.log("Message:", message);
      console.log("SessionId:", sessionId);
      console.log("TrackingSessionId:", trackingSessionId);
      console.log("LocationData:", locationData);
      console.log("ButtonType:", buttonType);
      console.log("Request Key:", requestKey);

      // Generate unique request ID for backend deduplication
      const requestId = generateRequestId(message, interactionType);

      // Use a longer timeout for chat requests since they involve LLM calls
      // Default timeout is 30s, but LLM calls can take 60-90s in some cases
      const response = await apiClient.post("/api/chat/structured", {
        message,
        sessionId,
        trackingSessionId, // Send tracking session ID
        contextData,
        source: origin, // "backend" | "llm"
        interaction_type: interactionType, // "button_click" | "user_input"
        locationData, // Add location data for "near me" searches
        type: buttonType, // Add button type for classification
        requestId, // Add unique request ID for deduplication
      }, {
        timeout: 90000, // 90 seconds for LLM processing
      });

      console.log("=== CHATBOT DEBUG: Full response received ===");
      console.log("response.status:", response.status);
      console.log("response.statusText:", response.statusText);
      console.log("response.headers:", response.headers);

      try {
        const responseStr = JSON.stringify(response.data);
        console.log("response.data (stringified length):", responseStr.length);
        console.log("response.data (parsed):", JSON.parse(responseStr));
      } catch (stringifyError) {
        console.error("=== ERROR: Cannot stringify response.data ===");
        console.error("Error:", stringifyError);
      }

      console.log("response.data.success:", response.data.success);
      console.log("response.data.structuredResponse:", response.data.structuredResponse);

      const renderResult = renderStructuredResponse(response.data);

      if (!renderResult.handled) {
        const fallbackContent =
          renderResult.reason === "missingStructuredResponse"
            ? "I received an incomplete response. Please try again."
            : "I received an invalid response format. Please try again.";

        const errorMessage = {
          role: "assistant",
          content: fallbackContent,
          timestamp: new Date().toISOString(),
          type: "text",
        };

        setMessages((prev) => [...prev, errorMessage]);
      }
    } catch (error) {
      console.error("=== CHATBOT ERROR: Failed to send message ===");
      console.error("Error type:", error.constructor?.name);
      console.error("Error:", error?.response?.data || error);
      console.error("Error message:", error?.message);
      console.error("Error stack:", error?.stack);

      if (error.response) {
        console.error("=== AXIOS RESPONSE ERROR ===");
        console.error("Error response status:", error.response.status);
        console.error("Error response statusText:", error.response.statusText);
        console.error("Error response headers:", error.response.headers);
        console.error("Error response data:", error.response.data);
      } else if (error.request) {
        console.error("=== AXIOS REQUEST ERROR (no response received) ===");
        console.error("Error request:", error.request);
      } else {
        console.error("=== GENERAL ERROR (not axios) ===");
        console.error("Error config:", error.config);
      }

      const structuredResult = renderStructuredResponse(error?.response?.data);
      if (structuredResult.handled) {
        return;
      }

      const serverMsg = getServerErrorMessage(error);

      console.log("Displaying error message:", serverMsg);

      const errorMessage = {
        role: "assistant",
        content: serverMsg,
        timestamp: new Date().toISOString(),
        type: "text",
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      console.log("=== Setting isLoading to false ===");
      setIsLoading(false);
      
      // Remove from pending requests
      pendingRequests.current.delete(requestKey);
      console.log("✅ Request completed and removed from pending:", requestKey);
    }
  };

  // Check if message is a location-based query
  // Uses fuzzy matching to handle spelling mistakes and variations
  const isLocationQuery = (message) => {
    if (!message || typeof message !== 'string') {
      return false;
    }

    // Normalize the message: trim and convert to lowercase
    const normalizedMessage = message.trim().toLowerCase();
    
    // First, try exact matching for performance (fast path)
    const locationKeywords = [
      'near me',
      'nearby',
      'near by',
      'nearest',
      'closest',
      'around me',
      'close to me',
      'stores near',
      'store near',
      'stores near me',
      'store near me',
      'near my location',
      'in my area',
      'near here',
      'close by',
      'close around',
      'near my place',
      'near my home',
      'near my current location',
      'find stores near me',
      'stores around',
      'store around me',
      'store nearby',
      'stores nearby',
      'closest store',
      'closest stores',
      'any store near me',
      'search nearby',
      'location',
      'locations',
      'my location',
      'current location',
      'where am i',
      'find near',
      'show nearby',
      'nearby stores',
      'nearby store',
      'nearby locations',
      'nearby location',
      'stores',
      'store'
    ];
    
    // Fast path: Check exact match first
    const exactMatch = locationKeywords.some(keyword => normalizedMessage.includes(keyword));
    
    if (exactMatch) {
      const matchedKeyword = locationKeywords.find(k => normalizedMessage.includes(k));
      console.log("🔍 Location check (exact match):", { 
        message: normalizedMessage, 
        isMatch: true,
        matchedKeyword
      });
      return true;
    }
    
    // Slow path: Use fuzzy matching for spelling mistakes
    // Threshold of 0.70 means 70% similarity required (handles 1-2 character typos more leniently)
    const fuzzyMatch = isFuzzyLocationQuery(normalizedMessage, 0.70);
    
    console.log("🔍 Location check (fuzzy match):", { 
      message: normalizedMessage, 
      isMatch: fuzzyMatch,
      method: fuzzyMatch ? 'fuzzy' : 'none'
    });
    
    return fuzzyMatch;
  };

  // Handle location-based search
  const handleLocationSearch = async (message) => {
    // Check for duplicate requests
    if (isDuplicateRequest(message, "user_input")) {
      console.warn("⚠️ Duplicate location search prevented:", message);
      return;
    }

    if (!isGeolocationAvailable()) {
      // Browser doesn't support geolocation
      const errorMessage = {
        role: "assistant",
        content: "Your browser doesn't support location services. Please search by city name instead, like 'stores in Delhi'.",
        timestamp: new Date().toISOString(),
        type: "text",
      };
      setMessages((prev) => [...prev, errorMessage]);
      setIsLoading(false);
      return;
    }

    try {
      // Show a loading message
      console.log("🔍 Requesting location...");
      
      // Request location permission
      const location = await requestUserLocation();
      console.log("✅ Location received:", location);
      
      // Send message with location data
      await sendMessage(message, null, "user_input", "llm", {
        lat: location.lat,
        long: location.long,
      });
    } catch (error) {
      console.error("❌ Location error:", error);
      
      // Location permission denied or failed
      let errorContent = "I couldn't access your location. ";
      
      if (error.code === 1) {
        errorContent += "You denied location permission. ";
      } else if (error.code === 2) {
        errorContent += "Location is unavailable. ";
      } else if (error.code === 3) {
        errorContent += "Location request timed out. ";
      }
      
      errorContent += "Please try searching by city name instead, like 'stores in Delhi'.";
      
      const errorMessage = {
        role: "assistant",
        content: errorContent,
        timestamp: new Date().toISOString(),
        type: "text",
      };
      setMessages((prev) => [...prev, errorMessage]);
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const message = inputMessage.trim();
    if (!message) return;

    // Prevent form submission while loading
    if (isLoading) {
      console.warn("⚠️ Form submission ignored - already processing a request");
      return;
    }

    // Check for duplicate requests
    if (isDuplicateRequest(message, "user_input")) {
      console.warn("⚠️ Duplicate form submission prevented:", message);
      return;
    }

    // Clear initial options when user sends first message
    if (initialOptions.length > 0) {
      setInitialOptions([]);
    }

    // Check if this is a location-based query
    if (isLocationQuery(message)) {
      handleLocationSearch(message);
    } else {
      sendMessage(message, null, "user_input", "llm");
    }
  };

  const isMoreDetailsButton = (btn) => {
    if (!btn) return false;
    const label = (btn.label || "").toLowerCase();
    const action = (btn.action || "").toLowerCase();
    return (
      label.includes("more details") ||
      label === "more details" ||
      action.includes("tell me more about") ||
      action.includes("more details about")
    );
  };

  const formatMoreDetailsUserText = (btn) => {
    const raw = (btn?.action || "").trim();
    if (!raw) return btn?.label || "More Details";
    // Remove [ID:xxx] suffix before displaying to user (internal implementation detail)
    const cleanText = raw.replace(/\s*\[ID:\d+\]\s*$/i, '').trim();
    // Capitalize first character for nicer display
    return cleanText.charAt(0).toUpperCase() + cleanText.slice(1);
  };

  const handleButtonClick = async (button) => {
    // Prevent button clicks while loading
    if (isLoading) {
      console.warn("⚠️ Button click ignored - already processing a request");
      return;
    }

    // Clear initial options when user interacts
    if (initialOptions.length > 0) {
      setInitialOptions([]);
    }

    // If it's a string (backward compatibility), just send it
    if (typeof button === "string") {
      // Check for duplicate requests
      if (isDuplicateRequest(button, "button_click")) {
        console.warn("⚠️ Duplicate button click prevented:", button);
        return;
      }

      if (isLocationQuery(button)) {
        console.log("🔍 Location query detected in button:", button);
        handleLocationSearch(button);
        return;
      }
      sendMessage(button, null, "button_click", "backend");
      return;
    }

    // Check if button action/label is a location query
    const messageToCheck = button.action || button.label;
    
    // Check for duplicate requests
    if (isDuplicateRequest(messageToCheck, "button_click")) {
      console.warn("⚠️ Duplicate button click prevented:", messageToCheck);
      return;
    }

    if (isLocationQuery(messageToCheck)) {
      console.log("🔍 Location query detected in button:", messageToCheck);
      handleLocationSearch(messageToCheck);
      return;
    }

    // Display a friendly text to the user; for "More Details" buttons, show the action text
    const displayContent = isMoreDetailsButton(button)
      ? formatMoreDetailsUserText(button)
      : button.label;

    const userMessage = {
      role: "user",
      content: displayContent,
      timestamp: new Date().toISOString(),
      type: "text",
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputMessage("");
    setIsLoading(true);

    // Send action to backend for processing, honoring button.source when present
    const origin = button.source || "backend";
    const buttonType = button.type || null;
    await sendMessageToBackend(button.action, null, "button_click", origin, null, buttonType);
  };

  const handleSuggestionClick = async (chip) => {
    // Prevent suggestion clicks while loading
    if (isLoading) {
      console.warn("⚠️ Suggestion click ignored - already processing a request");
      return;
    }

    // Check if the chip action/label is a location query
    const messageToCheck = chip.action || chip.label;
    
    // Check for duplicate requests
    if (isDuplicateRequest(messageToCheck, "button_click")) {
      console.warn("⚠️ Duplicate suggestion click prevented:", messageToCheck);
      return;
    }

    if (isLocationQuery(messageToCheck)) {
      console.log("🔍 Location query detected in suggestion:", messageToCheck);
      handleLocationSearch(messageToCheck);
      return;
    }

    // Clear initial options when user interacts
    if (initialOptions.length > 0) {
      setInitialOptions([]);
    }

    // Display label in user message, but send action to backend
    const userMessage = {
      role: "user",
      content: chip.label,
      timestamp: new Date().toISOString(),
      type: "text",
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputMessage("");
    setIsLoading(true);

    // Send action to backend/LLM for processing based on chip.source
    const origin = chip.source || "llm";
    const buttonType = chip.type || null;
    await sendMessageToBackend(chip.action, null, "button_click", origin, null, buttonType);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e); // Use handleSubmit to check for location queries
    }
  };

  const dismissKeyboard = () => {
    inputRef.current?.blur();
  };

  // Date formatting now handled by utils/dateFormatter with IST timezone

  // Format text messages (backward compatibility)
  const formatTextMessage = (message) => {
    if (!message?.content) return "";

    let content = message.content;

    // Convert markdown formatting to HTML
    content = content.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
    content = content.replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, "<em>$1</em>");
    content = content.replace(/__([^_]+)__/g, "<u>$1</u>");

    const lines = content.split("\n");
    const elements = [];

    for (let index = 0; index < lines.length; index++) {
      const line = lines[index];
      const trimmedLine = line.trim();

      if (trimmedLine === "") {
        elements.push(
          <div key={`line-${index}`} style={{ height: "12px" }}>
            &nbsp;
          </div>,
        );
      } else if (trimmedLine.startsWith("•") || trimmedLine.startsWith("-")) {
        elements.push(
          <div
            key={`line-${index}`}
            style={{
              marginLeft: "16px",
              marginBottom: "4px",
              lineHeight: "1.5",
            }}
            dangerouslySetInnerHTML={{ __html: trimmedLine }}
          />,
        );
      } else if (/^\d+\./.test(trimmedLine)) {
        elements.push(
          <div
            key={`line-${index}`}
            style={{
              marginLeft: "16px",
              marginBottom: "4px",
              lineHeight: "1.5",
            }}
            dangerouslySetInnerHTML={{ __html: trimmedLine }}
          />,
        );
      } else {
        elements.push(
          <div
            key={`line-${index}`}
            style={{
              marginBottom: "4px",
              lineHeight: "1.5",
            }}
            dangerouslySetInnerHTML={{ __html: line }}
          />,
        );
      }
    }

    return elements;
  };

  // Helper function to extract suggestions from a structured response
  const extractSuggestions = (structuredResponse) => {
    try {
      if (!structuredResponse) {
        console.log("extractSuggestions: No structuredResponse");
        return null;
      }

      if (!structuredResponse.blocks || !Array.isArray(structuredResponse.blocks)) {
        console.log("extractSuggestions: No blocks array");
        return null;
      }

      // Find the suggestions block (should be at the end)
      const suggestionsBlock = structuredResponse.blocks.find(
        (block) => block && block.type === "suggestions",
      );

      if (!suggestionsBlock) {
        console.log("extractSuggestions: No suggestions block found");
        return null;
      }

      if (!suggestionsBlock.chips || !Array.isArray(suggestionsBlock.chips)) {
        console.log("extractSuggestions: No chips array in suggestions block");
        return null;
      }

      console.log("extractSuggestions: Found", suggestionsBlock.chips.length, "suggestions");
      return suggestionsBlock.chips;
    } catch (error) {
      console.error("extractSuggestions: Error extracting suggestions", error);
      return null;
    }
  };

  return (
    <div className="chatbot-page">
      {/* Manager Contact Widget */}
      <ManagerContactWidget
        isOpen={isManagerWidgetOpen}
        onClose={() => setIsManagerWidgetOpen(false)}
        location={sessionData?.location}
        city={sessionData?.city}
      />

      {/* Full-screen Chat Interface */}
      <div className="chat-widget chat-fullscreen">
        {/* Header */}
        <div className="chat-header">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center space-x-2">
              <img
                src={BRAND_LOGO_DATA_URL}
                alt="BeanBot logo"
                width={40}
                height={40}
                className="rounded-lg shadow-md border border-white/20"
              />
              <span className="font-inter font-semibold text-white tracking-tight">
                BeanBot (Structured)
              </span>
            </div>
            <div className="flex items-center space-x-3">
              {/* Manager Contact Button - Show only if location and city are available */}
              {sessionData && sessionData.location && sessionData.city && (
                <button
                  onClick={() => setIsManagerWidgetOpen(true)}
                  className="bg-white/10 hover:bg-white/20 text-white rounded-full p-2 transition-colors"
                  title="Contact Manager"
                >
                  <Phone size={20} />
                </button>
              )}
              {/* Session Info */}
              {sessionData && (sessionData.location || sessionData.city || sessionData.tableNumber) && (
                <div className="flex flex-col items-end text-xs text-white/80">
                  {sessionData.location && sessionData.city && (
                    <div className="capitalize">{sessionData.location}, {sessionData.city}</div>
                  )}
                  {sessionData.tableNumber && (
                    <div>Table #{sessionData.tableNumber}</div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Chat Body */}
        <div className="chat-body">
          <div className="chat-messages" onClick={dismissKeyboard} onTouchStart={dismissKeyboard}>
            {messages.map((message, index) => {
              const messageKey = `msg-${index}-${message.timestamp || index}`;
              try {
                const suggestions =
                  message.type === "structured"
                    ? extractSuggestions(message.structuredResponse)
                    : null;

                return (
                  <React.Fragment key={messageKey}>
                    <div className={`message ${message.role === "user" ? "user" : "bot"} fade-in`}>
                      <div className="message-content">
                        <div className="message-body">
                          {message.type === "structured" ? (
                            <StructuredRenderer
                              structuredResponse={message.structuredResponse}
                              onButtonClick={handleButtonClick}
                              onSuggestionClick={handleSuggestionClick}
                              disabled={isLoading}
                              excludeSuggestions={true}
                              sessionData={sessionData}
                              metadata={message.metadata}
                              trackingSessionId={message.trackingSessionId || trackingSessionId}
                            />
                          ) : (
                            <div className="rich-text-message">
                              {message.role === "assistant"
                                ? formatTextMessage(message)
                                : message.content}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Render suggestions outside the message bubble */}
                    {suggestions && suggestions.length > 0 && (
                      <div className="inline-suggestions">
                        <div className="inline-suggestions__chips">
                          {suggestions.map((chip, chipIndex) => (
                            <button
                              key={chipIndex}
                              onClick={() => handleSuggestionClick(chip)}
                              disabled={isLoading}
                              className="suggestion-chip"
                            >
                              {chip.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </React.Fragment>
                );
              } catch (error) {
                console.error("=== ERROR RENDERING MESSAGE AT INDEX", index, "===");
                console.error("Message:", message);
                console.error("Error:", error);
                return (
                  <div key={messageKey} className="message bot fade-in">
                    <div className="message-content">
                      <div className="message-body">
                        <div className="rich-text-message structured-error">
                          Error displaying message. Please try again.
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }
            })}

            {/* Initial welcome options - shown only when no messages have been sent yet */}
            {initialOptions && initialOptions.length > 0 && messages.length <= 1 && (
              <div className="inline-suggestions">
                <div className="inline-suggestions__chips">
                  {initialOptions.map((chip, chipIndex) => (
                    <button
                      key={chipIndex}
                      onClick={() => handleSuggestionClick(chip)}
                      disabled={isLoading}
                      className="suggestion-chip"
                    >
                      {chip.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {isLoading && (
              <div className="message bot fade-in">
                <div className="message-content">
                  <div className="typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Input */}
        <div className="chat-input-wrapper">
          <form onSubmit={handleSubmit} className="chat-input">
            <input
              ref={inputRef}
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type your message..."
              disabled={isLoading}
              className="flex-1 px-3 py-2 border border-coffee-light rounded-lg focus:outline-none focus:ring-2 focus:ring-coffee-light focus:ring-opacity-50 font-inter"
            />
            <button
              type="submit"
              disabled={isLoading || !inputMessage?.trim()}
              className="ml-2 p-2 bg-coffee-brown text-white rounded-lg hover:bg-coffee-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Send size={16} />
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default ChatbotPageStructured;
